import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { Archive, ArchiveRestore, Clock, ArrowRight, BookOpen } from 'lucide-react';
import { saveTask, setCurrentTask } from '../../reducers/taskReducer';
import { updateTaskService } from '../../utils/taskService';
import { formatTaskDate } from '../../utils/formatter';

// Add memoized selectors
const selectChats = (state) => state.chats || [];

const selectTaskChats = createSelector(
  [selectChats],
  (chats) => chats.filter(chat => chat.taskId)
);

const TaskList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { getToken, userId } = useAuth();

  const { tasks } = useSelector((state) => state.taskList || { tasks: [] });
  const { projects } = useSelector((state) => state.projectList || { projects: [] });
  const taskChats = useSelector(selectTaskChats); // Use memoized selector
  const [activeTab, setActiveTab] = useState('unscheduled');
  const [, setIsTaskArchived] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    setIsTaskArchived(tasks.filter((task) => task.archived || task.completed));
  }, [tasks]);

  useEffect(() => {
    if (searchTerm && searchTerm.length > 2) {
      setActiveTab('');
    }
  }, [searchTerm]);

  const handleArchiveToggle = async (task) => {
    try {
      const updatedTask = await updateTaskService(getToken, userId, {
        id: task.id,
        title: task.title,
        archived: !task.archived
      });
      dispatch(saveTask({ task: updatedTask }));
    } catch (error) {
      console.error('Error updating task in component:', error);
    }
  };

  const handleTaskCompletionToggle = async (task) => {
    try {
      const updatedTask = await updateTaskService(getToken, userId, {
        id: task.id,
        title: task.title,
        completed: !task.completed,
      });
      dispatch(saveTask({ task: updatedTask }));
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleTaskClick = (task) => {
    dispatch(setCurrentTask(task.id));
    navigate(`/task/${task.id}`);
  }

  const handleTabClick = (tab) => {
    setSearchTerm('');
    setActiveTab(tab);
  }

  const filteredTasks = () => {
    switch (activeTab) {
      case 'unscheduled':
        return tasks.filter((task) => !taskChats.find(chat => chat.taskId === task.id) && !task.metaData?.projectId && !task.archived && !task.completed && task.status !== 'ready' && task.status !== 'cancelled' && task.status !== 'on-hold');
      case 'ready':
        return tasks.filter((task) => (task.status === 'ready' && !taskChats.find(chat => chat.taskId === task.id)) && !task.metaData?.projectId && !task.archived && !task.completed);
      case 'in-progress':
        return tasks.filter((task) => (task.status === 'in-progress' || taskChats.find(chat => chat.taskId === task.id)) && !task.metaData?.projectId && !task.archived && !task.completed);
      case 'completed':
        return tasks.filter((task) => task.completed && !task.archived && !task.metaData?.projectId);
      case 'archived':
        return tasks.filter((task) => task.archived && !task.metaData?.projectId);
      case 'on-hold':
        return tasks.filter((task) => task.status === 'on-hold' && !task.metaData?.projectId);
      case 'cancelled':
        return tasks.filter((task) => task.status === 'cancelled' && !task.metaData?.projectId);
      default:
        return tasks.filter((task) =>
          !task.archived &&
          task.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !(task.metaData?.projectId && projects.find(project => project.id === task.metaData.projectId && project.archived))
        );
    }
  };

  const getTaskDateRange = (task) => {
    if (task?.startBy || task?.endBy) {
      const startDate = formatTaskDate(task?.startBy);
      const endDate = formatTaskDate(task?.endBy);

      return (
        <span className="flex flex-row items-center">
          {startDate} {startDate && endDate && <ArrowRight size={15} />} {endDate}
        </span>
      );
    }
    return '';
  }

  const formatTaskStatusLabel = (status) => {
    switch (status) {
      case 'pending':
        return 'Unscheduled';
      case 'ready':
        return 'Ready';
      case 'in-progress':
        return 'In Progress';
      case 'on-hold':
        return 'On Hold';
      case 'cancelled':
        return 'Cancelled';
      case 'archived':
        return 'Archived';
      case 'completed':
        return 'Completed';
      default:
        return status;
    }
  }

  return (
    <div className="p-4 w-full overflow-y-auto">
      <h1 className="text-2xl font-bold mb-4">
        Task List 
        <span className="text-sm text-stone-500 ml-2">
          ({filteredTasks().length})
        </span>
      </h1>

      {/* Search for tasks */}
      <div className="flex flex-row items-center justify-start mb-4">
        <input 
          onChange={(e) => setSearchTerm(e.target.value)}
          data-amp-track-section="Task List"
          data-amp-track-action="Search_Task_Change"
          data-amp-track-description="Search Task Input Change"
          value={searchTerm}
          type="text" 
          placeholder="Search tasks" 
          className="w-full p-2 border border-stone-200 rounded-md mb-4" 
        />
      </div>
      
      {/* Tabs */}
      <div className="flex space-x-4 justify-between mb-4 overflow-x-auto py-2">
        <div className="flex justify-start gap-3">
          {['unscheduled', 'ready', 'in-progress', 'on-hold', 'cancelled'].map((tab) => (
            <button
              key={tab}
              onClick={() => handleTabClick(tab)}
              data-amp-track-section="Task List"
              data-amp-track-action="Filter_Task_Click"
              data-amp-track-description={`${tab.charAt(0).toUpperCase() + tab.slice(1)} Filter Click`}
              className={`flex flex-row items-center justify-start text-md font-semibold whitespace-nowrap gap-2 ml-0 px-2 py-2 border-solid border rounded-3xl border-stone-300 
                ${activeTab === tab 
                  ? ' text-white bg-stone-600 hover:bg-stone-500' 
                  : 'text-stone-600 bg-transparent hover:bg-sky-500 hover:text-white'}`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
        <div className="flex flex-row justify-end gap-2">
          {['completed', 'archived'].map((tab) => (
            <button 
              key={tab}
              onClick={() => handleTabClick(tab)}
              data-amp-track-section="Task List"
              data-amp-track-action="Filter_Task_Click"
              data-amp-track-description={`${tab.charAt(0).toUpperCase() + tab.slice(1)} Filter Click`}
              className={`flex flex-col justify-center text-sm whitespace-nowrap gap-2 ml-0 px-2 py-1 border-solid border rounded-3xl border-stone-300 
                ${activeTab === tab 
                  ? ' text-white bg-stone-600 hover:bg-stone-500' 
                  : 'text-stone-600 bg-transparent hover:bg-sky-500 hover:text-white'}`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Task List */}
      {filteredTasks().sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((task) => (
        <div key={task.id} className="mb-2 flex flex-row border-t border-stone-200 items-center justify-between cursor-pointer">
          
          <div className="flex flex-row items-start justify-start py-2">
            {activeTab !== 'archived' && task.status !== 'cancelled' && task.status !== 'on-hold' && (
              <input 
                type="checkbox" 
                checked={task.completed} 
                onChange={() => handleTaskCompletionToggle(task)}
                data-amp-track-section="Task List"
                data-amp-track-action="Mark_Done_Click"
                data-amp-track-description="Mark Done"
                className="mr-2 mt-1 hover:bg-slate-200 hover:cursor-pointer"
                />
            )}
            <div>
              {task?.metaData?.projectId && (
                <p className="flex items-center gap-1 mt-1 mb-1 text-xs text-stone-700">
                  <BookOpen size={16} />
                  <Link to={`/project/${task?.metaData?.projectId}`}
                    data-amp-track-section="Task List"
                    data-amp-track-action="View_Task_Project_Click"
                    data-amp-track-description="Open Task Project"
                  >
                    {task?.metaData?.projectTitle}
                  </Link>
                </p>
              )}
              <h3 className="font-semibold" onClick={() => handleTaskClick(task)}
                data-amp-track-section="Task List"
                data-amp-track-action="View_Task_Click"
                data-amp-track-description="Open Task"
              >
                  {searchTerm ? (
                    <>
                      {task.title.split(new RegExp(`(${searchTerm})`, 'gi')).map((part, index) => (
                        <span
                          key={index}
                          className={part.toLowerCase() === searchTerm.toLowerCase() ? 'bg-yellow-200' : ''}
                        >
                          {part}
                        </span>
                      ))}
                    </>
                  ) : (
                    task.title
                  )}
              </h3>
              <p className="text-sm text-gray-600" title={task.description}>{task.description?.substring(0, 200)}...</p>
              <div className="flex items-center mt-2 space-x-2">
                {task.endBy && (
                  <>
                    <Clock className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600 no-wrap">{getTaskDateRange(task)}</span>
                  </>
                )}
                <span
                  className={`text-xs px-2 py-1 rounded ${
                    task.status === "ready" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"
                  }`}
                >
                  {formatTaskStatusLabel(task.status)}
                </span>
              </div>

              <div className="flex flex-row items-center gap-2 mt-2">
                {task.metaData?.projectCategory?.map((category) => {
                  return (
                    <Link 
                      key={category.categoryId} 
                      to={`/project/category/${category.categoryId}`}
                      className="inline-block text-xs text-muted-foreground bg-stone-100 px-2 py-1 rounded-md hover:text-stone-700 hover:underline"
                    >
                      {category.categoryId.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                    </Link>
                  )
                })}
              </div>
            </div>
          </div>        
          <button 
            onClick={() => handleArchiveToggle(task)} 
            data-amp-track-section="Task List"
            data-amp-track-action="Archive_Task_Click"
            data-amp-track-description="Archive Task"
            className="bg-transparent hover:bg-stone-200 p-1 text-stone-500 hover:text-stone-700 ml-2"
          >
            {activeTab === 'archived' ? (
              <ArchiveRestore size={16} />
            ) : (
              <Archive size={16} />
            )}
          </button>
        </div>
      ))}
    </div>
  );
};

export default TaskList;
